// Updated TabbedConsole with Enhanced AI Rules Integration
import React, {useState, useRef, useEffect, useCallback} from 'react';
import Console from './Console';
import {useAuth} from '../../AuthProvider';
import AssetManagerPanel from './AssetManagerPanel';
import {CanisterInfo} from "../types";
import useIDEStore from './zustand/useIDEStore';

// Import the enhanced AI Rules component
import AIRulesTab from './AIRulesTab';

interface TabbedConsoleProps {
    activeFile: string;
    fileContent: string;
    onUpdateContent: (content: string) => void;
    onError: (error: string | null) => void;
    onSuccess: (message: string | null) => void;
    monaco: any;
    editorRef: React.MutableRefObject<any>;
    project?: any;
    userCanisters: CanisterInfo[];
    assetRefreshTrigger?: number;
    onAssetChange?: () => void;
}

interface AIDiffState {
    originalCode: string;
    modifiedCode: string;
    explanation: string;
}

const TabbedConsole: React.FC<TabbedConsoleProps> = ({
                                                         activeFile,
                                                         fileContent,
                                                         onUpdateContent,
                                                         onError,
                                                         onSuccess,
                                                         monaco,
                                                         editorRef,
                                                         project,
                                                         userCanisters,
                                                         assetRefreshTrigger,
                                                         onAssetChange
                                                     }) => {
    const [activeTab, setActiveTab] = useState<'console' | 'ai' | 'assets' | 'rules'>('console');
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [consoleHeight, setConsoleHeight] = useState<number>(260);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [startY, setStartY] = useState<number>(0);
    const [startHeight, setStartHeight] = useState<number>(260);
    const [isResizeHandleHovered, setIsResizeHandleHovered] = useState<boolean>(false);

    const {systemCanisters, userPrincipal} = useAuth();
    const consoleRef = useRef<HTMLDivElement>(null);

    // Get AI rules from IDE store
    const aiRules = useIDEStore(state => state.aiRules || []);

    // Find frontend canister if it exists
    const frontendCanisterId = userCanisters?.find(
        userCanister =>
            project?.canisters?.some(
                projectCanister =>
                    userCanister.principal.toText() === projectCanister.toText() &&
                    userCanister.canisterType === 'frontend'
            )
    )?.principal || null;

    const toggleCollapse = (): void => {
        setIsCollapsed(!isCollapsed);
    };

    const handleResizeStart = (e: React.MouseEvent): void => {
        e.preventDefault();
        setIsDragging(true);
        setStartY(e.clientY);
        setStartHeight(consoleHeight);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                const deltaY = startY - e.clientY;
                const newHeight = Math.max(100, Math.min(600, startHeight + deltaY));
                setConsoleHeight(newHeight);
            }
        };

        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, startY, startHeight]);

    // Enhanced function to get active AI rules context
    const getActiveRulesContext = (): string => {
        let context = '';

        // AI Rules (existing)
        const enabledRules = aiRules.filter(rule => rule.enabled && rule.status === 'active');
        if (enabledRules.length > 0) {
            const rulesByCategory = enabledRules.reduce((acc, rule) => {
                if (!acc[rule.category]) acc[rule.category] = [];
                acc[rule.category].push(rule);
                return acc;
            }, {} as Record<string, any[]>);

            context += '\n\n=== AI ASSISTANT RULES ===\n';
            context += 'Please follow these specific guidelines when providing assistance:\n\n';

            Object.entries(rulesByCategory).forEach(([category, rules]) => {
                context += `## ${category.toUpperCase()} GUIDELINES:\n`;
                rules.forEach(rule => {
                    context += `- ${rule.name}: ${rule.content}\n`;
                });
                context += '\n';
            });

            context += '=== END RULES ===\n\n';
        }

        // NEW: Add GitHub context integration here if needed
        // This would get GitHub sources from localStorage and build context
        try {
            const savedGitHub = localStorage.getItem('ide-github-sources');
            if (savedGitHub) {
                const githubSources = JSON.parse(savedGitHub);
                const activeGitHub = githubSources.filter((github: any) =>
                    github.status === 'active' && github.contentPreview
                );

                if (activeGitHub.length > 0) {
                    context += '\n\n=== GITHUB REPOSITORY GUIDELINES ===\n';
                    context += 'Follow these repository-specific guidelines:\n\n';

                    activeGitHub.forEach((github: any) => {
                        context += `## ${github.name.toUpperCase()}\n`;
                        context += `Repository: ${github.githubUrl}\n`;
                        context += `Guidelines: ${github.contentPreview.substring(0, 300)}...\n\n`;
                    });

                    context += '=== END GITHUB GUIDELINES ===\n\n';
                }
            }
        } catch (error) {
            console.warn('Failed to load GitHub context in TabbedConsole:', error);
        }

        return context;
    };


    return (
        <div
            ref={consoleRef}
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: isCollapsed ? '28px' : `${consoleHeight}px`,
                backgroundColor: '#fdf6e3',
                borderTop: '1px solid #8B4513',
                transition: isCollapsed ? 'height 0.3s ease' : 'none',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Resize handle */}
            {!isCollapsed && (
                <div
                    onMouseDown={handleResizeStart}
                    onMouseEnter={() => setIsResizeHandleHovered(true)}
                    onMouseLeave={() => setIsResizeHandleHovered(false)}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        cursor: 'ns-resize',
                        zIndex: 20,
                        backgroundColor: isDragging || isResizeHandleHovered
                            ? 'rgba(139, 69, 19, 0.3)'
                            : 'transparent'
                    }}
                />
            )}

            {/* Tab navigation */}
            <div style={{
                display: 'flex',
                backgroundColor: '#f3e5d8',
                borderBottom: isCollapsed ? 'none' : '1px solid #8B4513',
                height: isCollapsed ? '28px' : 'auto',
                alignItems: 'center'
            }}>
                {/* Console Tab */}
                <button
                    onClick={() => !isCollapsed && setActiveTab('console')}
                    style={{
                        padding: '4px 16px',
                        backgroundColor: !isCollapsed && activeTab === 'console' ? '#fdf6e3' : '#f3e5d8',
                        color: '#5C4033',
                        border: 'none',
                        borderRight: '1px solid #8B4513',
                        borderBottom: !isCollapsed && activeTab === 'console' ? 'none' : isCollapsed ? 'none' : '1px solid #8B4513',
                        borderTop: !isCollapsed && activeTab === 'console' ? '2px solid #8B4513' : 'none',
                        fontWeight: !isCollapsed && activeTab === 'console' ? 'bold' : 'normal',
                        cursor: isCollapsed ? 'default' : 'pointer',
                        opacity: isCollapsed ? 0.5 : 1,
                        height: isCollapsed ? '28px' : 'auto'
                    }}
                >
                    Console
                </button>

                {/* Smart Edit Tab */}
                <button
                    onClick={() => !isCollapsed && setActiveTab('ai')}
                    style={{
                        padding: '4px 16px',
                        backgroundColor: !isCollapsed && activeTab === 'ai' ? '#fdf6e3' : '#f3e5d8',
                        color: '#5C4033',
                        border: 'none',
                        borderRight: '1px solid #8B4513',
                        borderBottom: !isCollapsed && activeTab === 'ai' ? 'none' : isCollapsed ? 'none' : '1px solid #8B4513',
                        borderTop: !isCollapsed && activeTab === 'ai' ? '2px solid #8B4513' : 'none',
                        fontWeight: !isCollapsed && activeTab === 'ai' ? 'bold' : 'normal',
                        cursor: isCollapsed ? 'default' : 'pointer',
                        opacity: isCollapsed ? 0.5 : 1,
                        height: isCollapsed ? '28px' : 'auto'
                    }}
                >
                    Smart Edit (AI)
                </button>

                {/* Assets Tab */}
                <button
                    onClick={() => !isCollapsed && setActiveTab('assets')}
                    style={{
                        padding: '4px 16px',
                        backgroundColor: !isCollapsed && activeTab === 'assets' ? '#fdf6e3' : '#f3e5d8',
                        color: '#5C4033',
                        border: 'none',
                        borderRight: '1px solid #8B4513',
                        borderBottom: !isCollapsed && activeTab === 'assets' ? 'none' : isCollapsed ? 'none' : '1px solid #8B4513',
                        borderTop: !isCollapsed && activeTab === 'assets' ? '2px solid #8B4513' : 'none',
                        fontWeight: !isCollapsed && activeTab === 'assets' ? 'bold' : 'normal',
                        cursor: isCollapsed ? 'default' : 'pointer',
                        opacity: isCollapsed ? 0.5 : 1,
                        height: isCollapsed ? '28px' : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    Assets
                </button>

                {/* Enhanced AI Context Tab */}
                <button
                    onClick={() => !isCollapsed && setActiveTab('rules')}
                    style={{
                        padding: '4px 16px',
                        backgroundColor: !isCollapsed && activeTab === 'rules' ? '#fdf6e3' : '#f3e5d8',
                        color: '#5C4033',
                        border: 'none',
                        borderRight: '1px solid #8B4513',
                        borderBottom: !isCollapsed && activeTab === 'rules' ? 'none' : isCollapsed ? 'none' : '1px solid #8B4513',
                        borderTop: !isCollapsed && activeTab === 'rules' ? '2px solid #8B4513' : 'none',
                        fontWeight: !isCollapsed && activeTab === 'rules' ? 'bold' : 'normal',
                        cursor: isCollapsed ? 'default' : 'pointer',
                        opacity: isCollapsed ? 0.5 : 1,
                        height: isCollapsed ? '28px' : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14,2 14,8 20,8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    AI Context
                    {aiRules.filter(r => r.enabled).length > 0 && (
                        <span style={{
                            fontSize: '10px',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            borderRadius: '50%',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                        }}>
                            {aiRules.filter(r => r.enabled).length}
                        </span>
                    )}
                </button>

                {/* Collapse/Expand Toggle Button */}
                <button
                    onClick={toggleCollapse}
                    style={{
                        marginLeft: 'auto',
                        padding: '0 10px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                    }}
                >
                    <div style={{
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #8B4513',
                        borderRadius: '3px',
                        position: 'relative'
                    }}>
                        <div style={{
                            width: '8px',
                            height: '2px',
                            backgroundColor: '#8B4513'
                        }}/>
                        {isCollapsed && (
                            <div style={{
                                position: 'absolute',
                                width: '2px',
                                height: '8px',
                                backgroundColor: '#8B4513'
                            }}/>
                        )}
                    </div>
                </button>
            </div>

            {/* Tab content */}
            {!isCollapsed && (
                <div style={{
                    flex: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {activeTab === 'console' ? (
                        <div style={{flex: 1, overflow: 'auto'}}>
                            <Console/>
                        </div>
                    ) : activeTab === 'ai' ? (
                        <div style={{flex: 1, overflow: 'hidden'}}>
                            <SimpleAIPanel
                                activeFile={activeFile}
                                fileContent={fileContent}
                           cns     onUpdateContent={onUpdateContent}
                                onError={onError}
                                onSuccess={onSuccess}
                                monaco={monaco}
                                editorRef={editorRef}
                                aiRulesContext={getActiveRulesContext()}
                            />
                        </div>
                    ) : activeTab === 'assets' ? (
                        <div style={{flex: 1, overflow: 'hidden'}}>
                            <AssetManagerPanel
                                projectId={project.id}
                                userPrincipal={userPrincipal}
                                frontendCanisterId={frontendCanisterId}
                                onError={onError}
                                onSuccess={(message) => {
                                    onSuccess(message);
                                    onAssetChange?.();
                                }}
                                editorRef={editorRef}
                                activeFile={activeFile}
                            />
                        </div>
                    ) : (
                        <div style={{flex: 1, overflow: 'hidden'}}>
                            {/* 🚀 ENHANCED AI CONTEXT TAB */}
                            <AIRulesTab
                                onError={onError}
                                onSuccess={onSuccess}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Height indicator */}
            {isDragging && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    right: '10px',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(139, 69, 19, 0.8)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    pointerEvents: 'none',
                    zIndex: 30
                }}>
                    {consoleHeight}px
                </div>
            )}
        </div>
    );
};

const SimpleAIPanel: React.FC<any> = (props) => {
    const [prompt, setPrompt] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [lastApiError, setLastApiError] = useState<string | null>(null);

    // New state for enhanced feedback
    const [progress, setProgress] = useState<{ percent: number; message: string } | null>(null);
    const [showDiff, setShowDiff] = useState<boolean>(false);
    const [diffState, setDiffState] = useState<AIDiffState | null>(null);
    const [notification, setNotification] = useState<{
        message: string;
        type: 'success' | 'error' | 'info'
    } | null>(null);
    const [isAddingLogging, setIsAddingLogging] = useState<boolean>(false);

    const {systemCanisters, userPrincipal} = useAuth();

    // Notification helper function
    const showNotification = useCallback((message: string, type: 'success' | 'error' | 'info') => {
        setNotification({message, type});
        setTimeout(() => setNotification(null), 3000);
    }, []);

    const isMotokoFile = props.activeFile?.endsWith('.mo') || false;

    const handleAddLogging = async (): Promise<void> => {
        if (!systemCanisters.platformActor) {
            showNotification('Platform canister not initialized', 'error');
            props.onError('Platform canister not initialized');
            return;
        }

        if (!props.activeFile || !props.fileContent) {
            showNotification('No active file to edit', 'error');
            props.onError('No active file to edit');
            return;
        }

        // Check if file is Motoko
        if (!props.activeFile.endsWith('.mo')) {
            showNotification('Logging can only be added to Motoko (.mo) files', 'error');
            props.onError('Logging can only be added to Motoko (.mo) files');
            return;
        }

        // NEW: Check if logging infrastructure is already enabled
        const hasLoggingImport = props.fileContent.includes('import L "mo:cn-logger/logger"');
        const hasLoggerVariable = props.fileContent.includes('logger.') || props.fileContent.includes('let logger =');
        const hasLoggingMethods = props.fileContent.includes('logInfo') || props.fileContent.includes('getLogs');

        if (!hasLoggingImport || !hasLoggerVariable || !hasLoggingMethods) {
            // Logging infrastructure is not present
            showNotification('Logging infrastructure not found. Please enable logging first using the "📊 Enable Logging" button in the editor header.', 'error');
            props.onError('Logging infrastructure must be enabled first. Use the "📊 Enable Logging" button in the editor header to add the basic logging infrastructure, then use this AI feature to intelligently add log statements to your functions.');
            return;
        }

        setIsAddingLogging(true);
        setLastApiError(null);
        setProgress({percent: 5, message: 'Getting Claude API key from platform...'});

        try {
            // Get API key from platform instead of localStorage
            const apiKey = await getApiKeyFromPlatform();

            setProgress({percent: 15, message: 'Analyzing existing code for logging opportunities...'});

            const intelligentLoggingPrompt = `You are tasked with intelligently adding logging statements to this Motoko code that already has logging infrastructure in place.

IMPORTANT: The logging infrastructure (import, logger variable, and logging methods) is already present in this code. DO NOT add or modify the infrastructure.

Your task is to ONLY add appropriate logging statements throughout the existing functions using these methods:
- logger.info() - for general information, successful operations, function entry/exit
- logger.warn() - for potential issues, deprecation warnings, unusual conditions  
- logger.error() - for errors, exceptions, failed operations
- logger.dbg() - for detailed debugging information (NOTE: it's "dbg" not "debug")

CRITICAL IMPORT REQUIREMENTS:
Before using any primitive conversion functions in your log messages, check if the required imports exist:
- If using Int.toText() → ensure "import Int "mo:base/Int";" is present
- If using Nat.toText() → ensure "import Nat "mo:base/Nat";" is present  
- If using Bool.toText() → ensure "import Bool "mo:base/Bool";" is present
- If using Float.toText() → ensure "import Float "mo:base/Float";" is present
- If using Principal.toText() → ensure "import Principal "mo:base/Principal";" is present

If ANY required import is missing, you MUST add it at the top of the file with the other imports.

Guidelines for adding logs:
1. Add logger.info() at the start of public functions to log when they're called
2. Add logger.info() before return statements in successful operations
3. Add logger.error() in error handling blocks and for validation failures
4. Add logger.warn() for unusual conditions or deprecated code paths
5. Add logger.dbg() for internal state changes or complex calculations
6. Include relevant context in log messages (function names, parameter values, etc.)

Examples of good logging statements (with proper imports):
- logger.info("Starting balance transfer for user: " # userId);
- logger.error("Transfer failed: insufficient balance. Required: " # Nat.toText(amount) # ", Available: " # Nat.toText(balance));
- logger.warn("Large transfer amount detected: " # Nat.toText(amount));
- logger.dbg("Calculating fees for amount: " # Nat.toText(amount));
- logger.info("User verification status: " # Bool.toText(isVerified));
- logger.dbg("Current timestamp: " # Int.toText(Time.now()));

Rules:
- DO NOT modify existing function signatures or logic
- DO NOT add new functions or change the structure
- DO NOT modify existing imports unless adding required ones for logging
- Only add logging statements that enhance observability
- Use appropriate log levels based on the context
- Include meaningful context in log messages
- Don't over-log - focus on important events and decision points
- Always use logger.dbg() not logger.debug()
- Add necessary imports at the top if using primitive conversion functions

WORKFLOW:
1. First scan existing imports to see what's already available
2. Identify where you want to add logging with primitive values
3. Add any missing imports (Int, Nat, Bool, Float, Principal, etc.)
4. Add the logging statements with proper conversion functions

Please add intelligent logging statements throughout the code while preserving all existing functionality and ensuring all required imports are present.`;

            setProgress({percent: 30, message: 'Identifying optimal log placement locations...'});

            // Call the AI to add intelligent logging
            const response = await fetch('https://ai.coinnation.io/api/claude/fix', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    apiKey: apiKey, // Now using the apiKey fetched from platform
                    language: 'motoko',
                    currentCode: props.fileContent,
                    instructions: intelligentLoggingPrompt
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.body?.error || 'Failed to add intelligent logging');
            }

            setProgress({percent: 60, message: 'Processing AI suggestions...'});

            const data = await response.json();
            const fullResponse = data.body.fullResponse;

            // Extract code from response
            let updatedContent = fullResponse;
            const codeMatch = fullResponse.match(/```\w*\n([\s\S]*?)```/);
            if (codeMatch && codeMatch[1]) {
                updatedContent = codeMatch[1];
            }

            // Show diff view for review
            setDiffState({
                originalCode: props.fileContent,
                modifiedCode: updatedContent,
                explanation: fullResponse.split('```')[0] || 'AI has analyzed your code and suggested intelligent logging statements at key points.',
            });

            setShowDiff(true);
            setProgress({percent: 100, message: 'Intelligent logging suggestions ready for review!'});

            // Clear progress after a moment
            setTimeout(() => setProgress(null), 1000);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            setLastApiError(errorMessage);
            showNotification(`Error: ${errorMessage}`, 'error');
            props.onError(`Claude API error: ${errorMessage}`);
            setProgress(null);
        } finally {
            setIsAddingLogging(false);
        }
    };


    // Colors for styling
    const colors = {
        background: '#fdf6e3',
        border: '#8B4513',
        buttonPrimary: '#5C4033',
        buttonDisabled: '#9B5523',
        textPrimary: '#5C4033',
        textSecondary: '#8B4513',
        errorBg: '#fee2e2',
        errorText: '#dc2626',
        successBg: '#dcfce7',
        successText: '#16a34a',
        infoBg: '#dbeafe',
        infoText: '#2563eb',
        progressTrack: '#e9d8c6',
        progressBar: '#8B4513',
        diffAddBg: '#dcfce7',
        diffRemoveBg: '#fee2e2'
    };

    useEffect(() => {
        // Clear error when prompt changes
        if (lastApiError) {
            setLastApiError(null);
        }
    }, [prompt]);

    // Add this function:
    const getApiKeyFromPlatform = async (): Promise<string> => {
        if (!systemCanisters.platformActor) {
            throw new Error('Platform canister not initialized');
        }

        try {
            const result = await systemCanisters.platformActor.getClaudeApiKey();

            if ('ok' in result) {
                return result.ok;
            } else {
                throw new Error(result.err || 'Failed to retrieve Claude API key');
            }
        } catch (error) {
            console.error('Error fetching API key from platform:', error);
            throw new Error(`Claude API not configured: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    const handlePromptSubmit = async (): Promise<void> => {
        if (!systemCanisters.platformActor) {
            showNotification('Platform canister not initialized', 'error');
            props.onError('Platform canister not initialized');
            return;
        }

        if (!props.activeFile || !props.fileContent) {
            showNotification('No active file to edit', 'error');
            props.onError('No active file to edit');
            return;
        }

        setIsLoading(true);
        setLastApiError(null);
        setProgress({percent: 5, message: 'Getting Claude API key from platform...'});

        try {
            // Get API key from platform instead of localStorage
            const apiKey = await getApiKeyFromPlatform();

            setProgress({percent: 10, message: 'Preparing request...'});

            const extension = props.activeFile.split('.').pop()?.toLowerCase();
            let language = extension || '';

            // Map file extensions to language names that Claude will recognize
            if (extension === 'mo') language = 'motoko';
            else if (extension === 'tsx') language = 'typescript';
            else if (extension === 'jsx') language = 'javascript';

            setProgress({percent: 20, message: 'Analyzing code...'});

            const response = await fetch(`https://ai.coinnation.io/api/claude/fix`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    apiKey: apiKey, // Now using the apiKey fetched from platform
                    language: language,
                    currentCode: props.fileContent,
                    instructions: prompt
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.body?.error || 'Failed to fix code');
            }

            setProgress({percent: 60, message: 'Processing AI response...'});

            const data = await response.json();
            const fullResponse = data.body.fullResponse;

            setProgress({percent: 80, message: 'Preparing code comparison...'});

            // Extract code from response (simplified)
            let updatedContent = fullResponse;
            const codeMatch = fullResponse.match(/```\w*\n([\s\S]*?)```/);
            if (codeMatch && codeMatch[1]) {
                updatedContent = codeMatch[1];
            }

            // Show diff view instead of directly applying changes
            setDiffState({
                originalCode: props.fileContent,
                modifiedCode: updatedContent,
                explanation: fullResponse.split('```')[0] || '', // Use text before first code block as explanation
            });

            setShowDiff(true);
            setProgress({percent: 100, message: 'Ready for review'});

            // Clear progress after a moment
            setTimeout(() => setProgress(null), 1000);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            setLastApiError(errorMessage);
            showNotification(`Error: ${errorMessage}`, 'error');
            props.onError(`Claude API error: ${errorMessage}`);
            setProgress(null);
        } finally {
            setIsLoading(false);
        }
    };


    const applyChanges = () => {
        if (!diffState) return;

        props.onUpdateContent(diffState.modifiedCode);
        showNotification('Changes applied successfully', 'success');
        props.onSuccess('Code updated with Claude suggestions');

        // Reset state
        setShowDiff(false);
        setDiffState(null);
        setPrompt('');
    };

    const cancelChanges = () => {
        setShowDiff(false);
        setDiffState(null);
        showNotification('Changes cancelled', 'info');
    };

    // Render the code diff (simplified version - you may want to use a diff library)
    const renderDiff = () => {
        if (!diffState) return null;

        // Basic line-by-line diff
        const originalLines = diffState.originalCode.split('\n');
        const modifiedLines = diffState.modifiedCode.split('\n');

        // Find added and removed lines
        const maxLines = Math.max(originalLines.length, modifiedLines.length);
        const diffLines = [];

        for (let i = 0; i < maxLines; i++) {
            const originalLine = originalLines[i] || '';
            const modifiedLine = modifiedLines[i] || '';

            if (originalLine !== modifiedLine) {
                // Line was modified
                if (originalLine) {
                    diffLines.push({type: 'remove', content: originalLine});
                }
                if (modifiedLine) {
                    diffLines.push({type: 'add', content: modifiedLine});
                }
            } else {
                // Line is unchanged
                diffLines.push({type: 'unchanged', content: originalLine});
            }
        }

        return (
            <div style={{
                fontFamily: 'monospace',
                fontSize: '13px',
                backgroundColor: '#fff',
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                padding: '8px',
                height: '100%',
                overflow: 'auto'
            }}>
                {diffLines.map((line, index) => (
                    <div key={index} style={{
                        backgroundColor: line.type === 'add' ? colors.diffAddBg :
                            line.type === 'remove' ? colors.diffRemoveBg :
                                'transparent',
                        padding: '2px 4px',
                        whiteSpace: 'pre'
                    }}>
                        {line.type === 'add' ? '+ ' : line.type === 'remove' ? '- ' : '  '}
                        {line.content}
                    </div>
                ))}
            </div>
        );
    };

    // Render progress bar
    const renderProgressBar = () => {
        if (!progress) return null;

        return (
            <div style={{marginBottom: '10px'}}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: colors.textSecondary,
                    marginBottom: '4px'
                }}>
                    <span>{progress.message}</span>
                    <span>{progress.percent.toFixed(0)}%</span>
                </div>
                <div style={{
                    height: '4px',
                    backgroundColor: colors.progressTrack,
                    borderRadius: '2px',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        height: '100%',
                        width: `${progress.percent}%`,
                        backgroundColor: colors.progressBar,
                        transition: 'width 0.3s ease'
                    }}/>
                </div>
            </div>
        );
    };

    // Render notification toast
    const renderNotification = () => {
        if (!notification) return null;

        let bgColor, textColor;
        switch (notification.type) {
            case 'success':
                bgColor = colors.successBg;
                textColor = colors.successText;
                break;
            case 'error':
                bgColor = colors.errorBg;
                textColor = colors.errorText;
                break;
            case 'info':
                bgColor = colors.infoBg;
                textColor = colors.infoText;
                break;
        }

        return (
            <div style={{
                position: 'absolute',
                bottom: '16px',
                right: '16px',
                backgroundColor: bgColor,
                color: textColor,
                padding: '8px 16px',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                maxWidth: '300px',
                fontSize: '13px',
                zIndex: 100,
                animation: 'fadeIn 0.3s ease'
            }}>
                {notification.message}
            </div>
        );
    };

    return (
        <div style={{
            height: '100%',
            padding: '10px',
            backgroundColor: colors.background,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
            }}>
                <div style={{color: colors.textPrimary, fontSize: '16px', fontWeight: 'bold'}}>
                    ICP Studio: AI Assistant
                </div>
                <div style={{display: 'flex', gap: '8px'}}>
                    {/* Add Logging Button - Only enabled for .mo files */}
                    {/* Updated Add Logging Button - Now for intelligent log placement */}
                    <button
                        onClick={handleAddLogging}
                        disabled={isLoading || isAddingLogging || !isMotokoFile || showDiff}
                        title={!isMotokoFile
                            ? "Only available for Motoko (.mo) files"
                            : "Intelligently add logging statements to your functions. Note: Logging infrastructure must be enabled first."}
                        style={{
                            backgroundColor: (!isMotokoFile || isAddingLogging || isLoading || showDiff) ? colors.buttonDisabled : colors.buttonPrimary,
                            color: '#FFF5EE',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 10px',
                            fontSize: '12px',
                            cursor: (!isMotokoFile || isAddingLogging || isLoading || showDiff) ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            opacity: (!isMotokoFile || isAddingLogging || isLoading || showDiff) ? 0.7 : 1
                        }}
                    >
                        {isAddingLogging && (
                            <span style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                border: '2px solid rgba(255,255,255,0.3)',
                                borderTopColor: '#FFF5EE',
                                animation: 'spin 1s linear infinite',
                                display: 'inline-block'
                            }}/>
                        )}
                        {/* Updated log icon to be more intelligent-looking */}
                        {!isAddingLogging && (
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M9 12l2 2 4-4"/>
                                <path
                                    d="M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1H3c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1h18z"/>
                                <path
                                    d="M3 18c-.552 0-1 .448-1 1v0c0 .552.448 1 1 1h18c.552 0 1-.448 1-1v0c0-.552-.448-1-1-1H3z"/>
                            </svg>
                        )}
                        {isAddingLogging ? 'Analyzing...' : 'Smart Logging'}
                    </button>
                </div>
            </div>


            {/* Error Message */}
            {lastApiError && (
                <div style={{
                    padding: '6px',
                    backgroundColor: colors.errorBg,
                    color: colors.errorText,
                    borderRadius: '4px',
                    fontSize: '13px',
                    marginBottom: '8px'
                }}>
                    Error: {lastApiError}
                </div>
            )}

            {/* Progress Bar */}
            {renderProgressBar()}

            {/* Main Content - either Diff Preview or Prompt Input */}
            {showDiff && diffState ? (
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    {/* Explanation of changes */}
                    {diffState.explanation && (
                        <div style={{
                            padding: '8px',
                            backgroundColor: colors.infoBg,
                            color: colors.infoText,
                            borderRadius: '4px',
                            fontSize: '13px',
                            marginBottom: '8px',
                            maxHeight: '100px',
                            overflow: 'auto'
                        }}>
                            <strong>AI Explanation:</strong> {diffState.explanation}
                        </div>
                    )}

                    {/* Diff View */}
                    <div style={{flex: 1, marginBottom: '8px', overflow: 'hidden'}}>
                        {renderDiff()}
                    </div>

                    {/* Actions */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px'
                    }}>
                        <button
                            onClick={cancelChanges}
                            style={{
                                backgroundColor: '#f3e5d8',
                                color: colors.textPrimary,
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={applyChanges}
                            style={{
                                backgroundColor: colors.buttonPrimary,
                                color: '#FFF5EE',
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        >
                            Apply Changes
                        </button>
                    </div>
                </div>
            ) : (
                /* Prompt Input */
                <>
                    <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="Enter instructions for the assistant (e.g., 'Add error handling to this function' or 'Optimize this code')"
                        style={{
                            flex: 1,
                            padding: '6px',
                            borderRadius: '4px',
                            border: `1px solid ${colors.border}`,
                            backgroundColor: '#fff',
                            resize: 'none',
                            fontFamily: 'inherit',
                            fontSize: '13px',
                            marginBottom: '10px'
                        }}
                        disabled={isLoading || isAddingLogging}
                    />

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '16px'
                    }}>
                        <div style={{fontSize: '12px', color: colors.textSecondary}}>
                            {props.activeFile ? `Editing: ${props.activeFile.split('/').pop()}` : 'No file selected'}
                        </div>
                        <button
                            onClick={handlePromptSubmit}
                            disabled={isLoading || !prompt || !props.activeFile}
                            style={{
                                backgroundColor: isLoading || !prompt || !props.activeFile ? colors.buttonDisabled : colors.buttonPrimary,
                                color: '#FFF5EE',
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isLoading || !prompt || !props.activeFile ? 'not-allowed' : 'pointer',
                                opacity: isLoading || !prompt || !props.activeFile ? 0.7 : 1,
                                fontSize: '13px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            {isLoading && (
                                <span style={{
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    border: '2px solid rgba(255,255,255,0.3)',
                                    borderTopColor: '#FFF5EE',
                                    animation: 'spin 1s linear infinite',
                                    display: 'inline-block'
                                }}/>
                            )}
                            {isLoading ? 'Processing...' : 'Generate Changes'}
                        </button>
                    </div>
                </>
            )}

            {/* Toast Notification */}
            {renderNotification()}

            {/* CSS for animations */}
            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
};


export default TabbedConsole;